import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

/**
 * LoadingSpinner Component
 * Location: frontend/src/components/common/LoadingSpinner.js
 *
 * Reusable loading spinner component with size and color variants.
 * Uses Ant Design Spin component with customizable styling.
 *
 * @param {string} size - Spinner size: 'small' (16px), 'medium' (24px), 'large' (32px), 'xlarge' (48px)
 * @param {string} color - Spinner color (default: primary brand color)
 * @param {string} label - Accessible label for screen readers
 * @param {string} text - Optional loading text to display below spinner
 * @param {boolean} fullScreen - If true, centers spinner in full screen overlay
 * @param {React.CSSProperties} style - Custom styles for the container
 *
 * @example
 * // Basic usage
 * <LoadingSpinner />
 *
 * @example
 * // With size and text
 * <LoadingSpinner size="large" text="Loading your data..." />
 *
 * @example
 * // Full screen loading overlay
 * <LoadingSpinner fullScreen text="Please wait..." />
 *
 * @example
 * // Custom color and size
 * <LoadingSpinner size="xlarge" color="#52c41a" label="Loading reports" />
 */
const LoadingSpinner = ({
  size = 'medium',
  color = 'var(--rlc-text-accent)',
  label = 'Loading',
  text = null,
  fullScreen = false,
  style = {},
}) => {
  // Size mappings for spinner icon
  const sizeMap = {
    small: 16,
    medium: 24,
    large: 32,
    xlarge: 48,
  };

  const iconSize = sizeMap[size] || sizeMap.medium;

  // Custom loading icon with specified size and color
  const loadingIcon = (
    <LoadingOutlined
      style={{
        fontSize: iconSize,
        color: color,
      }}
      spin
    />
  );

  // Container styles
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    ...style,
  };

  // Full screen overlay styles
  const fullScreenStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 9999,
    ...containerStyle,
  };

  // Text styles
  const textStyle = {
    fontSize: size === 'small' ? '12px' : size === 'large' || size === 'xlarge' ? '16px' : '14px',
    color: '#595959',
    fontWeight: 500,
  };

  const spinnerContent = (
    <>
      <Spin
        indicator={loadingIcon}
        aria-label={label}
        data-testid="loading-spinner"
      />
      {text && (
        <div style={textStyle} data-testid="loading-text">
          {text}
        </div>
      )}
    </>
  );

  if (fullScreen) {
    return (
      <div style={fullScreenStyle} data-testid="loading-spinner-fullscreen">
        {spinnerContent}
      </div>
    );
  }

  return (
    <div style={containerStyle} data-testid="loading-spinner-container">
      {spinnerContent}
    </div>
  );
};

/**
 * LoadingSpinnerInline Component
 * Inline spinner for use within text or smaller UI elements
 *
 * @example
 * <LoadingSpinnerInline /> Loading...
 */
export const LoadingSpinnerInline = ({ size = 'small', color = 'var(--rlc-text-accent)' }) => {
  const sizeMap = {
    small: 14,
    medium: 16,
    large: 18,
  };

  const iconSize = sizeMap[size] || sizeMap.small;

  return (
    <LoadingOutlined
      style={{
        fontSize: iconSize,
        color: color,
        marginRight: '6px',
        verticalAlign: 'middle',
      }}
      spin
      data-testid="loading-spinner-inline"
    />
  );
};

export default LoadingSpinner;
